import React, { Component } from "react";
import { matchPath, Redirect, Route, Router, Switch } from "react-router-dom";
import withIdleTimer from "../common/WithIdleTimer";
import {
  CANADA_ENGLISH,
  ENGLISH,
  LINCOLN_ENGLISH,
  SALESCODE_KEY,
} from "../common/Constants";
import NADealer from "./pure/NADealer";
import { applyBrandChange } from "./pure/changeBrand";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import AccessoriesWrapper from "../components/accessories/AccessoriesWrapper";
import { publish } from "../common/PubSub";
import ErrorScreen from "../errors/ErrorScreen";
import DealerDashboard from "../components/dashboard/dealerDashBoard/DealerDashboard";
import ProtectedRoute from "../common/ProtectedRoute";
import Footer from "../shared/Footer/Footer";
import LocalizedInfoClient from "../clients/LocalizedInfoClient";
import { logMsg } from "../common/Logger";
import PageError from "../errors/PageError";
import "../styles/sharedStyles.scss";
import Help from "../components/help/Help";
import NADealerReporting from "../components/reporting/NADealerReporting";
import { withTranslation } from "react-i18next";
import DealerSearch from "../components/fieldView/DealerSearch";
import { IS_CANADA_MARKET, IS_USA_MARKET } from "../utils/EmpUtil";
import FieldReporting from "../components/fieldView/FieldReporting";
import Reporting from "../components/reporting/Reporting";
import BusinessSettings from "../components/businessSetting/BusinessSettings";
import DeliveryTools from "../components/deliveryTools/DeliveryTools";
import SearchResultsWrapper from "../components/customerHandling/search/SearchResultsWrapper";
import ReleaseNotes from "../components/releaseNotes/ReleaseNotes";
import styles from "./App.module.scss";
import OrderDetailsPage from "../orders/order-details/OrderDetailsPage";
import { UserContext } from "../shared/context/userContext";
import EMPFieldSupportBar from "../shared/components/EMPFieldSupportBar/EMPFieldSupportBar";
import { ModalProvider } from "../shared/components/EMPModal/ModalContext";
import Orders from "../orders/Orders";
import OnboardingPageWithStripe from "../onboarding/OnboardingPageWithStripe";
import { getFieldSupportBrand } from "../utils/fieldSupportBrand";
import { isUsFieldSupportContext } from "../utils/usFieldSupport";

class FieldViewApp extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    const brand = getFieldSupportBrand(props.userDetails.divisionCode);
    const user = new NADealer(
      { ...props.userDetails, brand, dealerBrand: brand },
      props.history
    );
    this.state = {
      renderApp: false,
      user,
      shouldNavigationBeRestricted: false,
      brand,
    };
    this.salesCode = sessionStorage.getItem(SALESCODE_KEY);
    this.localizedInfo = {};
    this.dealerChanged = this.dealerChanged.bind(this);

    // Listening for route change
    this.unlisten = this.props.history.listen(() => {
      if (this.props.history.location.pathname === "/dealer-search") {
        this.changeDealer();
      }
      this.setNavigationRestriction();
    });
  }

  componentWillUnmount() {
    this.unlisten?.();
  }

  componentDidMount() {
    // need this for error popups so that user isn't re-authorized as regular dealer
    this.props.history.push("/dealer-search");
    // Initialize UserContext with basic user details immediately
    this.initializeUserContext();
    this.getLocalizedInfo();
  }

  initializeUserContext = () => {
    const { updateUser } = this.context;
    if (updateUser && this.props.userDetails) {
      // Initialize with basic user details from props to prevent null errors
      updateUser(this.props.userDetails);
    }
  };

  updateUserContext = () => {
    const { updateUser } = this.context;
    if (updateUser && this.state.user && this.state.user.permissions) {
      // Create a serializable user object for the context
      const userContextData = {
        ...this.props.userDetails, // Include original userDetails for backward compatibility
        // Override with current state values (only if they exist)
        salesCode: this.state.user.salesCode,
        brand: this.state.user.brand,
        dealerBrand: this.state.user.dealerBrand,
        market: this.state.user.market,
        dealerName: this.state.user.dealerName,
        dealerId: this.state.user.dealerId,
        userType: this.state.user.userType,
        roles: this.state.user.roles,
        permissions: this.state.user.permissions,
        // Add any other relevant user properties that components might need
        currentDealer: this.state.user.currentDealer,
        dealerGroup: this.state.user.dealerGroup,
      };
      updateUser(userContextData);
    }
  };

  getLocalizedInfo() {
    LocalizedInfoClient.getLocalizedInfo(this.state.user)
      .then((response) => {
        this.localizedInfo = Object.assign(
          {},
          { ...this.localizedInfo },
          { ...response }
        );
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        if (this.state.brand === "L") {
          this.props.i18n.changeLanguage(LINCOLN_ENGLISH);
          this.state.user.setLanguageCode(this.props.i18n.language);
        } else if (IS_CANADA_MARKET(this.state.user.market)) {
          this.props.i18n.changeLanguage(CANADA_ENGLISH);
          this.state.user.setLanguageCode(this.props.i18n.language);
        } else {
          this.props.i18n.changeLanguage(ENGLISH);
          this.state.user.setLanguageCode(this.props.i18n.language);
        }

        this.setNavigationRestriction();
        this.setState({ renderApp: true }, () => {
          // Initialize UserContext after everything is loaded and state is set
          this.updateUserContext();
        });
      });
  }

  isMainViewingSDLR = (dealer) => {
    // Returns true if a main dealer is currently viewing an SDLR
    return this.state.user.isLoggedInUserMDLR && dealer.dealerType === "SDLR";
  };

  setNavigationRestriction = () => {
    const isAllowedPath = this.isCurrentPathAllowedToShowHeaderAndFooter(
      this.props.history.location.pathname
    );
    this.setState({ shouldNavigationBeRestricted: !isAllowedPath });
  };

  isCurrentPathAllowedToShowHeaderAndFooter = (currentPathName) => {
    const paths = [
      "/dashboard",
      "/pricing/accessories",
      "/",
      "/dealer-reporting",
      "/help",
      "/error",
      "/dealer-search",
      "/field-reporting",
      "/reporting",
      "/business-settings",
      "/delivery-tools",
      "/delivery-tools/download",
      "/delivery-tools/times",
      "/delivery-tools/tasks",
      "/ecommerce-releases",
      "/customer-handling",
      "/onboarding",
      "/orders",
    ];

    if (paths.includes(currentPathName)) {
      return true;
    } else {
      const allowedOrderDetails = matchPath(currentPathName, {
        path: `/dealer/:commonId/customer-reservations/order-details/:id`,
      });

      const allowedPurchaseDetails = matchPath(currentPathName, {
        path: `/dealer/:commonId/customer-reservations/purchase-details/:id`,
      });

      const allowedModelEOrderDetails = matchPath(currentPathName, {
        path: `/dealer/:commonId/customer-reservations/model-e-details/:id`,
      });

      const allowedCustomerHandlingTabs = matchPath(currentPathName, {
        path: `/customer-handling/:selectedTab`,
      });

      const allowedSearchResultsWrapper = matchPath(currentPathName, {
        path: `/search/:keyword`,
      });

      return (
        !!allowedOrderDetails ||
        !!allowedPurchaseDetails ||
        !!allowedModelEOrderDetails ||
        !!allowedCustomerHandlingTabs ||
        !!allowedSearchResultsWrapper
      );
    }
  };

  updateCurrentDealer(dealer, showAll = false) {
    const updatedUser = this.state.user.updateCurrentDealer(dealer, showAll);

    this.setState(
      {
        user: updatedUser,
        brand: updatedUser.brand,
      },
      () => {
        this.state.user.setLanguageCode(this.props.i18n.language);
        // Update UserContext after dealer/brand change
        this.updateUserContext();
        publish("brandChanged", updatedUser.brand);
      }
    );
  }

  dealerChanged(dealer, showAll = false) {
    this.updateCurrentDealer(dealer, showAll);
  }

  changeBrand = (brandCode) => {
    const isUsaMarket = IS_USA_MARKET(this.state.user.market);
    const { updatedUser } = applyBrandChange(this.state.user, brandCode, {
      isUsaMarket,
      includeFordHubMdlr: false,
      includeLanguage: false,
    });
    this.setState({ user: updatedUser, brand: brandCode }, () => {
      this.updateUserContext();
      publish("brandChanged", brandCode);
    });
  };

  changeDealer = () => {
    const user = new NADealer(this.props.userDetails, this.props.history);
    const brand = getFieldSupportBrand(user.divisionCode);
    user.updateBrand(brand);
    user.dealerBrand = brand;

    this.props.i18n.changeLanguage(
      brand === "L"
        ? LINCOLN_ENGLISH
        : IS_CANADA_MARKET(user.market)
          ? CANADA_ENGLISH
          : ENGLISH
    );
    user.setLanguageCode(this.props.i18n.language);

    this.setState({ user, brand }, () => {
      // Update UserContext after state change
      this.updateUserContext();
      publish("brandChanged", brand);
    });
  };

  render() {
    const isUsFieldSupport = isUsFieldSupportContext(this.state.user);

    return this.state.renderApp ? (
      <Router history={this.props.history}>
        <ModalProvider>
          <EMPFieldSupportBar />
          <section className={styles.fullHeightLayout}>
            <div className={styles.fullHeightItem}>
              {!this.state.dealerGroupError &&
                !this.state.shouldNavigationBeRestricted && (
                  <GlobalNavBar
                    {...this.props}
                    urlPath={window.location.pathname}
                    user={this.state.user}
                    changeBrand={this.changeBrand}
                    changeDealer={this.changeDealer}
                    fromDealerSearch={
                      this.props.history.location.pathname ===
                        "/dealer-search" ||
                      this.props.history.location.pathname ===
                        "/field-reporting" ||
                      this.props.history.location.pathname ===
                        "/ecommerce-releases"
                    }
                  />
                )}
            </div>
            <div className={styles.fullHeightItem}>
              <Switch>
                <ProtectedRoute
                  exact
                  path="/dashboard"
                  component={withIdleTimer(DealerDashboard)}
                  perform={["dashBoardPage"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                    onDealerChanged: this.dealerChanged,
                    lang: this.lang,
                  }}
                  parentProps={this.props}
                  condition={true}
                />
                <ProtectedRoute
                  exact
                  path="/pricing/accessories"
                  component={withIdleTimer(AccessoriesWrapper)}
                  perform={["accessoriesPage"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                    changeBrand: this.changeBrand,
                    onDealerChanged: this.dealerChanged,
                    localizedInfo: this.localizedInfo,
                    lang: this.lang,
                  }}
                  parentProps={this.props}
                  condition={true}
                />
                <ProtectedRoute
                  exact
                  path="/reporting"
                  component={withIdleTimer(Reporting)}
                  perform={["reportingPage"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                    lang: this.lang,
                  }}
                  parentProps={this.props}
                  condition={true}
                />
                <ProtectedRoute
                  exact
                  path="/business-settings"
                  component={withIdleTimer(BusinessSettings)}
                  perform={["businessSettingsPage"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  condition={true}
                  customprops={{
                    ...this.props,
                    isMainViewingSDLR: this.isMainViewingSDLR,
                    user: this.state.user,
                    lang: this.lang,
                    dealerGroup: this.state.user.dealerGroup,
                    localizedInfo: this.localizedInfo,
                    permissions: this.state.user.permissions.rolePermissions,
                  }}
                />

                <ProtectedRoute
                  exact
                  path="/customer-handling"
                  component={withIdleTimer(Orders)}
                  perform={["customerHandlingPage"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                    localizedInfo: this.localizedInfo,
                    onDealerChanged: this.dealerChanged,
                    lang: this.lang,
                  }}
                  parentProps={this.props}
                  condition={true}
                />

                <Route path="/customer-handling/:orderId">
                  <OrderDetailsPage />
                </Route>

                <ProtectedRoute
                  exact
                  path="/dealer-reporting"
                  component={withIdleTimer(NADealerReporting)}
                  perform={["dealerReporting:view"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                    lang: this.lang,
                  }}
                  parentProps={this.props}
                  condition={true}
                />

                <ProtectedRoute
                  exact
                  path="/dealer-search"
                  component={withIdleTimer(DealerSearch)}
                  perform={["dealerSearch"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                    lang: this.lang,
                    brand: this.state.brand,
                    updateCurrentDealer: this.updateCurrentDealer.bind(this),
                    updateBrand: (brandCode) => this.changeBrand(brandCode),
                  }}
                  parentProps={this.props}
                  condition={true}
                />
                <ProtectedRoute
                  exact
                  path="/field-reporting"
                  component={withIdleTimer(FieldReporting)}
                  perform={["fieldReporting"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                    lang: this.lang,
                    brand: this.state.brand,
                  }}
                  parentProps={this.props}
                  condition={false}
                />
                <ProtectedRoute
                  path="/delivery-tools"
                  component={withIdleTimer(DeliveryTools)}
                  perform={["modelEDeliveryToolsTab"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{ user: this.state.user, lang: this.lang }}
                  parentProps={this.props}
                  condition={true}
                />
                <ProtectedRoute
                  exact
                  path="/onboarding"
                  component={withIdleTimer(OnboardingPageWithStripe)}
                  perform={["onboarding:nonvehicle"]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                    localizedInfo: this.localizedInfo,
                    onDealerChanged: this.dealerChanged,
                    lang: this.lang,
                  }}
                  parentProps={this.props}
                  condition={true}
                />
                <Route
                  path="/ecommerce-releases"
                  component={withIdleTimer(() => (
                    <ReleaseNotes {...this.props} user={this.state.user} />
                  ))}
                />
                <ProtectedRoute
                  exact
                  path="/search/:keyword"
                  component={withIdleTimer(SearchResultsWrapper)}
                  perform={[]}
                  permissions={this.state.user.permissions.rolePermissions}
                  customprops={{
                    user: this.state.user,
                  }}
                  parentProps={this.props}
                  condition={true}
                />
                <Route
                  exact
                  path="/"
                  render={() => (
                    <Redirect
                      to={{
                        pathname: "/dealer-search",
                      }}
                    />
                  )}
                />
                <Route
                  exact
                  path="/help"
                  component={withIdleTimer(() => (
                    <Help {...this.props} market={this.state.user.market} />
                  ))}
                />
                <Route
                  exact
                  path="/error"
                  render={() => <ErrorScreen {...this.props} />}
                />
                <Route component={withIdleTimer(PageError)} />
              </Switch>
            </div>
            <div className={styles.fullHeightItem}>
              {!this.state.shouldNavigationBeRestricted && (
                <span className="noPrint">
                  <ModalProvider>
                    <Footer
                      user={this.state.user}
                      footerTitle={this.props.t("Footer.baseTitle")}
                      helpSupportTitle="Help & Support"
                      permissions={this.state.user.permissions}
                      simplified={isUsFieldSupport}
                    />
                  </ModalProvider>
                </span>
              )}
            </div>
          </section>
        </ModalProvider>
      </Router>
    ) : (
      ""
    );
  }
}

export default withTranslation("emp")(FieldViewApp);
